import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge, { UrgencyFlag } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { fmtTime, fmtDate } from "@/lib/format";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 11 }, (_, i) => 8 + i); // 8am - 6pm

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay() || 7; // Sun=0 → 7
  if (day !== 1) date.setDate(date.getDate() - (day - 1));
  date.setHours(0, 0, 0, 0);
  return date;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarPage() {
  const nav = useNavigate();
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [techFilter, setTechFilter] = useState("all");

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const load = async () => {
    const [j, t] = await Promise.all([
      api.get("/calendar/week", { params: { start: isoDate(weekStart) } }),
      api.get("/team"),
    ]);
    setJobs(j.data);
    setTeam(t.data);
  };

  useEffect(() => { load(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamColor = (id) => team.find((m) => m.id === id)?.color || "#64748B";

  const STATUS_BG = {
    unscheduled: "#94A3B8",   // slate-400 (grey)
    scheduled: "#2563EB",     // blue-600
    in_progress: "#EA580C",   // orange-600
    done: "#16A34A",          // green-600
  };
  const statusBg = (s) => STATUS_BG[s] || "#64748B";

  const visible = techFilter === "all" ? jobs : techFilter === "unassigned" ? jobs.filter((j) => !j.tech_id) : jobs.filter((j) => j.tech_id === techFilter);

  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const openSlot = (date, hour) => {
    const time = `${String(hour).padStart(2, "0")}:00`;
    nav(`/jobs/new?date=${isoDate(date)}&time=${time}`);
  };

  const jobsAt = (date, hour) => {
    const dStr = isoDate(date);
    return visible.filter((j) => {
      if (j.scheduled_date !== dStr) return false;
      const h = parseInt((j.scheduled_time || "").split(":")[0] || "0", 10);
      return h === hour;
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">Schedule</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={techFilter} onValueChange={setTechFilter}>
            <SelectTrigger className="h-11 w-44" data-testid="cal-tech-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All technicians</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {team.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                    {m.full_name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => shift(-1)} className="h-11 w-11 p-0 rounded-xl" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></Button>
          <Button variant="outline" onClick={() => setWeekStart(getMonday(new Date()))} className="h-11 px-3 rounded-xl" data-testid="cal-today">Today</Button>
          <Button variant="outline" onClick={() => shift(1)} className="h-11 w-11 p-0 rounded-xl" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>

      <div className="text-sm text-slate-500">
        Week of <b className="text-slate-800">{weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</b>
      </div>

      {/* Status legend (calendar blocks) */}
      <div className="flex flex-wrap gap-2 text-xs">
        <LegendPill color="#94A3B8" label="Unscheduled" />
        <LegendPill color="#2563EB" label="Scheduled" />
        <LegendPill color="#EA580C" label="In Progress" />
        <LegendPill color="#16A34A" label="Complete" />
        <LegendPill color="#DC2626" label="Emergency (red left)" />
      </div>

      {/* Tech legend */}
      {team.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          <span className="mr-1 font-semibold">Team:</span>
          {team.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-slate-200">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: m.color }} />
              {m.full_name}
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <div className="min-w-[820px]">
          {/* Day headers */}
          <div className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b border-slate-200 bg-slate-50">
            <div />
            {days.map((d, i) => (
              <div key={i} className="px-2 py-3 text-center">
                <div className="text-xs font-semibold text-slate-500 uppercase">{DAYS[i]}</div>
                <div className="text-base font-bold text-slate-900">{d.getDate()}</div>
              </div>
            ))}
          </div>

          {HOURS.map((h) => (
            <div key={h} className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b border-slate-100">
              <div className="text-[11px] text-slate-500 font-semibold px-2 py-2 border-r border-slate-100">
                {h > 12 ? h - 12 : h}{h >= 12 ? "pm" : "am"}
              </div>
              {days.map((d, i) => {
                const cellJobs = jobsAt(d, h);
                return (
                  <div
                    key={i}
                    className="relative min-h-[64px] border-r border-slate-100 last:border-r-0 hover:bg-blue-50/40 cursor-pointer p-1 transition-colors group"
                    onClick={() => cellJobs.length === 0 && openSlot(d, h)}
                    data-testid={`cell-${isoDate(d)}-${h}`}
                  >
                    {cellJobs.length === 0 ? (
                      <div className="opacity-0 group-hover:opacity-100 h-full w-full flex items-center justify-center text-slate-400">
                        <Plus className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {cellJobs.map((j) => (
                          <Link
                            key={j.id}
                            to={`/jobs/${j.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block text-[11px] rounded-md p-1.5 truncate font-semibold text-white"
                            style={{
                              backgroundColor: statusBg(j.status),
                              borderLeft: j.urgency === "emergency" ? "4px solid #DC2626" : "none",
                              paddingLeft: j.urgency === "emergency" ? "8px" : undefined,
                            }}
                            data-testid={`cal-job-${j.id}`}
                          >
                            <div className="truncate">{fmtTime(j.scheduled_time)} {j.title}</div>
                            <div className="truncate opacity-90 font-normal">{j.customer_name} · {j.tech_name}</div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="text-sm text-slate-500">
        Tip: tap any empty slot to schedule a new job at that time.
      </div>

      {/* Unscheduled today */}
      {visible.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mt-4 mb-2" style={{ fontFamily: "Outfit" }}>All jobs this week</h2>
          <div className="space-y-2">
            {visible.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} className="block bg-white rounded-2xl border border-slate-200 p-4 hover:border-blue-300 transition" data-testid={`week-job-${j.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><UrgencyFlag urgency={j.urgency} /><div className="font-semibold truncate">{j.title}</div></div>
                    <div className="text-sm text-slate-500 mt-0.5">{fmtDate(j.scheduled_date)} {j.scheduled_time && `at ${fmtTime(j.scheduled_time)}`} • {j.tech_name}</div>
                  </div>
                  <StatusBadge status={j.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LegendPill({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-slate-200">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}
